import React, { useState, useRef, useEffect } from 'react';
import { Play, Volume2, VolumeX } from 'lucide-react';

const Manifesto = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    // Intentar reproducir el video automáticamente cuando el componente se monta
    if (videoRef.current) {
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Video se reproduce automáticamente
            setIsPlaying(true);
          })
          .catch((error) => {
            // Autoplay bloqueado por el navegador
            console.log('Autoplay bloqueado:', error);
            setIsPlaying(false);
          });
      }
    }
  }, []);

  const handlePlayClick = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleVideoPlay = () => {
    setIsPlaying(true);
  };

  const handleVideoPause = () => {
    setIsPlaying(false);
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  return (
    <section id="manifesto" className="py-32 bg-gradient-to-b from-black via-gray-950 to-black relative overflow-hidden">
      {/* CSS para mantener controles siempre visibles */}
      <style>{`
        .video-always-controls::-webkit-media-controls-panel {
          opacity: 1 !important;
        }
        .video-always-controls {
          /* Mantener controles siempre visibles */
        }
        .video-always-controls::-webkit-media-controls {
          opacity: 1 !important;
          visibility: visible !important;
        }
      `}</style>

      {/* Background effects */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at center, rgba(20, 184, 166, 0.1) 0%, transparent 70%)'
        }} />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-teal-400 text-sm uppercase tracking-widest font-semibold mb-4 block">
            Nuestro Manifiesto
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8">
            Por Qué Existimos
          </h2>
        </div>

        {/* Video player real */}
        <div className="relative mb-16">
          <div className="relative aspect-video bg-gradient-to-br from-gray-900 to-black rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-teal-500/10">
            <video 
              ref={videoRef}
              controls={isPlaying}
              autoPlay
              muted
              playsInline
              className="w-full h-full object-cover video-always-controls"
              poster="https://images.unsplash.com/photo-1648337564744-f919c7c2fc02?w=1200"
              onPlay={handleVideoPlay}
              onPause={handleVideoPause}
            >
              <source src="/Manifiesto-USL.mp4" type="video/mp4" />
              Tu navegador no soporta el elemento de video.
            </video>

            {/* Custom Play Overlay - Solo visible cuando está pausado */}
            {!isPlaying && (
              <div 
                className="absolute inset-0 flex items-center justify-center bg-black/40 cursor-pointer transition-opacity duration-300"
                onClick={handlePlayClick}
              >
                <button className="group relative">
                  <div className="absolute inset-0 bg-teal-500/20 rounded-full blur-2xl animate-pulse" />
                  <div className="relative w-24 h-24 bg-gradient-to-r from-teal-500 to-teal-600 rounded-full flex items-center justify-center shadow-2xl shadow-teal-500/50 hover:scale-110 transition-transform duration-300">
                    <Play size={32} className="text-black ml-1" fill="currentColor" />
                  </div>
                </button>
              </div>
            )}

            {/* Botón de Volumen Personalizado - Esquina Superior Derecha */}
            {isPlaying && (
              <button
                onClick={toggleMute}
                className="absolute top-4 right-4 z-20 p-3 bg-black/60 backdrop-blur-sm rounded-full border border-white/20 hover:bg-teal-500/80 transition-all duration-300 group"
              >
                {isMuted ? (
                  <VolumeX size={24} className="text-white group-hover:scale-110 transition-transform" />
                ) : (
                  <Volume2 size={24} className="text-white group-hover:scale-110 transition-transform" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Manifesto text */}
        <div className="text-center space-y-8">
          <blockquote className="text-3xl md:text-4xl lg:text-5xl font-light text-white leading-tight">
            “No nacimos para seguir el sistema.
            <br />
            <span className="font-bold bg-gradient-to-r from-teal-400 to-teal-600 bg-clip-text text-transparent">
              Nacimos para trascenderlo.
            </span>
            ”
          </blockquote>

          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            <span className="text-white font-medium">Urpe School of Liberty</span>
            <br />
            Donde los emprendedores aprenden libertad.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Manifesto;
